import { TableCell, TableRow, Avatar, AvatarFallback, Checkbox } from "@libs/zapi-ui/shadcn";
import { AppButton } from "@libs/zapi-ui/custom";
import { TUser } from "../services/schemas";
import { useRevokeInvitation } from "../services/useRevokeInvitation";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { getInitials } from "../lib/getInitials";
import { useTranslation } from "react-i18next";
import { STATUS_KEYS, getMemberRoles } from "../const/const";
import { useResendInvitation } from "../services/useResendInvitation";

type TeamTableRowProps = {
  member: TUser;
  organizationId: number;
  selectedRows: number[];
  handleSelectRow: (id: number) => void;
  currentUserId?: number;
  handleOpenManageAccess: (member: TUser) => void;
};

export function TeamTableRow(props: TeamTableRowProps) {
  const { member, organizationId, selectedRows, handleSelectRow, currentUserId, handleOpenManageAccess } = props;
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const { mutate: cancelInvitation, isPending: isLoadingCancel, isSuccess: isSuccessCancel } = useRevokeInvitation();
  const { mutate: resendInvitation, isPending: isLoadingResend } = useResendInvitation();

  const isCurrentUser = member.id === currentUserId;
  const isPending = member.status === "pending";
  const isExpired = member.status === "expired";
  const actionsDisabled = isCurrentUser || isPending || isExpired;

  function handleCancelInvitation() {
    if (!organizationId) return;
    cancelInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
          toast.success(t("team.inviteCancelled"));
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? t("team.inviteCancelFailed"));
        }
      }
    );
  }

  function handleResendInvitation() {
    if (!organizationId) return;
    resendInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          toast.success(t("team.inviteResent"));
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? t("team.inviteResendFailed"));
        }
      }
    );
  }

  if (isSuccessCancel) return null;

  return (
    <TableRow data-state={selectedRows.includes(member.id) && "selected"}>
      <TableCell>
        <Checkbox
          checked={selectedRows.includes(member.id)}
          onCheckedChange={() => handleSelectRow(member.id)}
          aria-label={t("team.selectRow", { name: `${member.firstName} ${member.lastName}` })}
          disabled
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-3">
          <Avatar>
            <AvatarFallback>{getInitials(member.firstName, member.lastName)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {member.firstName} {member.lastName} {isCurrentUser && t("team.you")}
            </div>
            <div className="text-[14px] text-muted-foreground">{member.email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>{getMemberRoles(t).find((role) => role.value === member.role)?.label || member.role}</TableCell>
      <TableCell>{t("team.projectCount", { count: member.projects?.length || 0 })}</TableCell>
      <TableCell className={isExpired ? "text-red-500" : ""}>{STATUS_KEYS[member.status as keyof typeof STATUS_KEYS] ? t(STATUS_KEYS[member.status as keyof typeof STATUS_KEYS]) : member.status}</TableCell>
      {/* <TableCell>-</TableCell> */}
      <TableCell>{member.createdAt.toLocaleDateString()}</TableCell>
      <TableCell>
        {isPending || isExpired ? (
          <div className="flex items-center space-x-2">
            <AppButton
              color="zinc150"
              size="sm"
              disabled={isLoadingCancel || isLoadingResend}
              onClick={handleResendInvitation}
            >
              {isLoadingResend ? t("team.resending") : t("team.resendInvite")}
            </AppButton>
            {isPending && (
              <AppButton
                color="red400"
                size="sm"
                onClick={handleCancelInvitation}
                disabled={isLoadingCancel || isLoadingResend}
              >
                {isLoadingCancel ? t("team.cancelling") : t("team.cancelInvite")}
              </AppButton>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <AppButton
              color="zinc150"
              size="sm"
              onClick={() => handleOpenManageAccess(member)}
              disabled={actionsDisabled || isLoadingResend}
            >
              {t("team.manageAccess")}
            </AppButton>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
